/**
 * SubtitlesOnlyPanel
 *
 * Main panel for the "Subtitles Only" sub-tab.
 * Orchestrates all section components via SubtitlesProvider (no prop drilling).
 * Mobile-first layout: single column, sections stack vertically.
 */

import React, { memo } from 'react';
import { Captions } from 'lucide-react';
import { SubtitlesProvider } from './SubtitlesContext';
import { useSubtitles } from './SubtitlesContext.context';
import { MediaUploadSection } from './sections/MediaUploadSection';
import { SourceLanguageSection } from './sections/SourceLanguageSection';
import { TargetLanguagesSection } from './sections/TargetLanguagesSection';
import { OutputModeSection } from './sections/OutputModeSection';
import { FormatsSection } from './sections/FormatsSection';
import { OptionsSection } from './sections/OptionsSection';
import { HardBurnConfirmation } from './sections/HardBurnConfirmation';
import { GenerateButton } from './sections/GenerateButton';
import { ResultSection } from './sections/ResultSection';
import { useTranslation } from '@/hooks/useTranslation';

// ============================================================================
// Inner component — consumes SubtitlesContext
// ============================================================================

function SubtitlesOnlyPanelInner(): JSX.Element {
  const { t } = useTranslation();
  const { state, showForm, isGenerating, isCompleted } = useSubtitles();
  const { mediaFile } = state;

  const isHardBurn = state.outputMode === 'hard_burn';

  return (
    <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-8">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-purple-900/30 border border-purple-500/20">
            <Captions className="h-5 w-5 sm:h-6 sm:w-6 text-purple-400" />
          </div>
          <h2 className="text-xl sm:text-2xl font-bold text-white">
            {t('subtitles.panel.title', { defaultValue: 'Subtitle Generator' })}
          </h2>
        </div>
        <p className="text-sm text-white/60">
          {t('subtitles.panel.subtitle', {
            defaultValue:
              'Upload any video or audio file and get multi-language subtitle files (SRT, VTT, ASS) in a single ZIP.',
          })}
        </p>
      </div>

      {/* Result section — always rendered so progress bar stays visible */}
      {(isGenerating ||
        isCompleted ||
        state.status === 'cancelled' ||
        state.status === 'error') && (
        <div className="mb-6">
          <ResultSection />
        </div>
      )}

      {/* Form — hidden while generating or completed */}
      {showForm && (
        <div className="space-y-4 sm:space-y-6">
          {/* Step 1: Upload */}
          <MediaUploadSection />

          {/* Step 2: Languages — only after a file is chosen */}
          {mediaFile && (
            <>
              <SourceLanguageSection />
              <TargetLanguagesSection />
            </>
          )}

          {/* Step 3: Output config — only after languages are selected */}
          {mediaFile && state.targetLanguages.length > 0 && (
            <>
              <OutputModeSection />
              <FormatsSection />

              {/* Hard burn: show style editor + confirmation instead of options */}
              {isHardBurn ? <HardBurnConfirmation /> : <OptionsSection />}
            </>
          )}

          {/* Generate CTA */}
          {mediaFile && (
            <div className="flex justify-center pt-2">
              <GenerateButton />
            </div>
          )}
        </div>
      )}

      {/* Error state retry — show form again so user can adjust and retry */}
      {state.status === 'error' && (
        <div className="mt-6 space-y-4 sm:space-y-6">
          <OutputModeSection />
          <FormatsSection />
          {isHardBurn ? <HardBurnConfirmation /> : <OptionsSection />}
          <div className="flex justify-center pt-2">
            <GenerateButton />
          </div>
        </div>
      )}

      {/* YouTube publish entry-points removed during API v3 migration
          (2026-05-26). Will be re-added by the OAuth-based wizard. */}
    </div>
  );
}

// ============================================================================
// Public export — wraps inner in provider
// ============================================================================

export const SubtitlesOnlyPanel: React.FC = memo(function SubtitlesOnlyPanel() {
  return (
    <SubtitlesProvider>
      <SubtitlesOnlyPanelInner />
    </SubtitlesProvider>
  );
});
